import { Selector } from '@ngxs/store';
import { AppState } from './app.state';
import { AppStateModel } from './app.model';
import { BusinessStatus } from '../../_interfaces/business.interface';

export class AppSelectors {
  @Selector([AppState])
  static title(state: AppStateModel) {
    return state.pageTitle;
  }

  @Selector([AppState])
  static sideNavOpen(state: AppStateModel) {
    return state.sideNavOpen;
  }

  @Selector([AppState])
  static sessionExpired(state: AppStateModel) {
    return state.sessionExpired;
  }

  @Selector([AppState])
  static userDetails(state: AppStateModel) {
    return state.userDetails;
  }

  @Selector([AppState])
  static userEmail(state: AppStateModel) {
    return state.userDetails?.email;
  }

  @Selector([AppState])
  static userPhone(state: AppStateModel) {
    return state.userDetails?.phone;
  }

  @Selector([AppState])
  static isAccountLocked(state: AppStateModel): boolean | undefined {
    return state.accountLocked;
  }

  @Selector([AppState])
  static isPhoneVerified(state: AppStateModel) {
    // If userDetails doesn't exist yet, return undefined to signal "loading"
    // If accountLocked is true, bypass phone verification
    if (state.userDetails === undefined) {
      return state.accountLocked === true ? true : undefined;
    }
    return state.userDetails.phone_verified;
  }

  @Selector([AppState])
  static hasSubscription(state: AppStateModel) {
    return state.businessDetails?.subscription ?? state.accountLocked;
  }

  @Selector([AppState])
  static hasBusinessCert(state: AppStateModel) {
    return state.businessDetails?.cert_file ?? state.accountLocked;
  }

  @Selector([AppState])
  static businessDetails(state: AppStateModel) {
    return state.businessDetails;
  }

  @Selector([AppState])
  static bizId(state: AppStateModel) {
    return state.businessDetails?.id;
  }

  @Selector([AppState])
  static hasAcceptedLatestTerms(state: AppStateModel) {
    return state.userDetails?.terms_accepted;
  }

  @Selector([AppState])
  static idVerificationStatus(state: AppStateModel) {
    return state.userDetails?.idnum_status;
  }

  @Selector([AppState])
  static hasNavigationAccess(state: AppStateModel) {
    return state.userDetails?.biz.status === BusinessStatus.Active && !state.userDetails?.biz.beta;
  }

  @Selector([AppState])
  static shouldDisableBodyScroll(state: AppStateModel): boolean {
    // Disable scroll whenever user doesn't have navigation access
    // This is the inverse of hasNavigationAccess
    return !(state.userDetails?.biz?.status === BusinessStatus.Active && !state.userDetails?.biz.beta);
  }

  @Selector([AppState])
  static shouldShowStatusBlockOverlay(state: AppStateModel): boolean {
    const status = state.userDetails?.biz.status;
    // Show for ANY status that is NOT Active and NOT PaymentPending
    // This includes beta users in onboarding/error states
    return status !== BusinessStatus.Active && status !== BusinessStatus.PaymentPending;
  }

  @Selector([AppState])
  static shouldShowPaymentPendingOverlay(state: AppStateModel): boolean {
    const status = state.userDetails?.biz.status;
    const beta = state.userDetails?.biz.beta;
    // Show ONLY when PaymentPending and NOT beta
    // Beta users in PaymentPending see RegistrationBlock instead
    return status === BusinessStatus.PaymentPending && !beta;
  }

  @Selector([AppState])
  static shouldShowBetaRegistrationOverlay(state: AppStateModel): boolean {
    const status = state.userDetails?.biz.status;
    const beta = state.userDetails?.biz.beta;
    // Show ONLY when beta AND (Active OR PaymentPending)
    // Beta users in other states (New, Rejected, etc.) see StatusBlock instead
    return beta === true && (status === BusinessStatus.Active || status === BusinessStatus.PaymentPending);
  }

  /**
   * @deprecated Use shouldShowBetaRegistrationOverlay instead
   */
  @Selector([AppState])
  static isBetaRegistration(state: AppStateModel) {
    return (
      state.userDetails?.biz.beta &&
      (state.userDetails?.biz.status === BusinessStatus.PaymentPending ||
        state.userDetails?.biz.status === BusinessStatus.Active)
    );
  }
}
